import React from 'react';
import PropTypes from 'prop-types';
import Hotkeys from 'react-hot-keys';

import AppBar from '@mui/material/AppBar';
import Fab from '@mui/material/Fab';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Typography from '@mui/material/Typography';
import {styled} from '@mui/system';

import GlobalContext from './helpers/context';
import BreadCrumbs from './component/molecule/BreadCrumbs';
import HotKeyChip from './component/atoms/HotKeyChip';
import LinkButton from './component/atoms/LinkButton';


const AppHeaderComponent = styled('div')(({ theme }) =>({
    flexGrow: 1,
    minWidth: theme.custom.min_width
}));

const MenuButtonComponent = styled(IconButton)({
    marginRight: 0
});

const MenuComponent = styled(Menu)({
    marginTop: 40
});

const UserIconComponent = styled(AccountCircle)({
    marginRight: 10
});

const UserNameComponent = styled(Fab)(({ theme }) =>({
    color: 'inherit',
    fontSize: theme.custom.font.large,
    fontWeight: 400,
    textTransform: 'none',
    boxShadow: 'none'
}));

const MenuTitleComponent = styled(Typography)({
    flexGrow: 1
});


const app_menu_items = {
    'users': {'label': 'User Administration', 'roles': ['admin']},
    'workspaces': {'label': 'Workspaces', 'roles': ['admin', 'manager', 'reviewer']},
    'suspicious_deletes': {'label': 'Suspicious Deletes', 'roles': ['admin', 'manager', 'reviewer']}
};


class AppHeader extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            app_menu_open: false,
            user_menu_open: false
        };

        this.user_menu_selected_element = null;
        this.user_menu_open = Boolean(this.user_menu_selected_element);

        this.appMenuIconRef = React.createRef();
        this.userMenuIconRef = React.createRef();
    }

    componentDidUpdate(){
        this.context.checkUserAccess();
    }

    handleAppMenuIconClick = () => {
        this.setState({ app_menu_open: !this.state.app_menu_open });
    };

    handleAppMenuIconClose = () => {
        this.setState({ app_menu_open: false });
    };

    getAppMenuTitle(){
        const item_key = this.props.pathName.split('?')[0].split('/')[1].replace('/', '');
        if (item_key in app_menu_items){
            return app_menu_items[item_key]['label'];
        }
    }

    userHasAccess = (roles) => {
        let mapping_roles = [];
        if (this.props.user){
            mapping_roles = this.props.user.roles.filter((item) => {
              return roles.indexOf(item) >= 0;
            });
        }
        return mapping_roles.length;
    };

    handleUserMenuIconClick = () => {
        this.setState({ user_menu_open: !this.state.user_menu_open });
    };

    handleUserMenuIconClose = () => {
        this.setState({ user_menu_open: false });
    };

    logout = () => {
        this.handleUserMenuIconClose();
        this.props.logoutCallBack();
    };

    render() {
        return (
            <GlobalContext.Consumer>{() => (
                <AppHeaderComponent>
                    <AppBar position="static">
                        <Toolbar>
                            <MenuButtonComponent
                                color="inherit"
                                aria-label="Menu"
                                data-bt="app_menu_icon"
                                ref={this.appMenuIconRef}
                                onClick={this.handleAppMenuIconClick}
                                size="large">
                                <MenuIcon />
                            </MenuButtonComponent>
                            <HotKeyChip hotKey="alt+m" />
                            <Hotkeys keyName="alt+m" onKeyDown={this.handleAppMenuIconClick} />
                            <Menu
                                id="app-menu"
                                anchorEl={this.appMenuIconRef.current}
                                open={this.state.app_menu_open}
                                onClose={this.handleAppMenuIconClose}
                            >
                                {Object.keys(app_menu_items).map(key => {
                                    if(this.userHasAccess(app_menu_items[key]['roles'])) {
                                        return (
                                            <MenuItem key={key} onClick={() => {this.handleAppMenuIconClose()}}>
                                                <LinkButton text={app_menu_items[key]['label']} linkStyle="text" data-bt={`menu_item_${key}`}
                                                    linkData={{pathname: `/${key}`}} />
                                            </MenuItem>
                                        );
                                    }
                                })}
                            </Menu>
                            <MenuTitleComponent variant="h6" color="inherit">
                                {this.getAppMenuTitle()}
                            </MenuTitleComponent>

                            {this.props.user && (
                                <section>
                                    <UserNameComponent variant="extended" color="primary"
                                         ref={this.userMenuIconRef}
                                         aria-label="Account of current user"
                                         aria-controls="user-menu"
                                         aria-haspopup="true"
                                         data-bt="user_menu"
                                         onClick={this.handleUserMenuIconClick}>
                                        <UserIconComponent />
                                        {this.props.user.name}
                                    </UserNameComponent>
                                    <MenuComponent
                                        id="user-menu"
                                        anchorEl={this.userMenuIconRef.current}
                                        anchorOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        keepMounted
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        open={this.state.user_menu_open}
                                        onClose={this.handleUserMenuIconClose}
                                    >
                                        <MenuItem key="change_password" onClick={() => {this.handleUserMenuIconClose()}}>
                                            <LinkButton text="Change password" linkStyle="text" data-bt="change_password"
                                                    linkData={{pathname: `/change_password`}} />
                                        </MenuItem>
                                        <MenuItem key="logout" onClick={this.logout}>Logout</MenuItem>
                                    </MenuComponent>
                                    <HotKeyChip hotKey="alt+u" />
                                    <Hotkeys keyName="alt+u" onKeyDown={this.handleUserMenuIconClick} />
                                </section>
                            )}
                        </Toolbar>
                    </AppBar>
                    <BreadCrumbs pathName={this.props.pathName} params={this.props.params}/>
                </AppHeaderComponent>
            )}
            </GlobalContext.Consumer>
        );
    }

}

AppHeader.contextType = GlobalContext;

AppHeader.propTypes = {
    logoutCallBack: PropTypes.func,
    user: PropTypes.object,
    pathName: PropTypes.string,
    params: PropTypes.object
};


export default AppHeader;
